class SubscriptionPurchase extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector("[data-variants-json]").textContent);
    const relatedPackageProductsJson = this.querySelector("[data-related-package-products-json]");
    this.relatedPackageProducts = relatedPackageProductsJson
      ? JSON.parse(relatedPackageProductsJson.textContent)
      : [];
    this.subscriptionProductJson = this.querySelector("[data-subscription-product-json]");
    this.subscriptionProduct = this.subscriptionProductJson
      ? JSON.parse(this.subscriptionProductJson.textContent)
      : null;
    this.hasSubscriptionProduct = this.dataset.hasSubscriptionProduct === "true";
    this.oneTimeVariantId = Number(this.dataset.oneTimeVariantId);
    this.subscriptionVariantId = Number(this.dataset.subscriptionVariantId) || null;
    this.variantInput = this.querySelector("[data-variant-id]");
    this.quantityInput = this.querySelector("[data-quantity]");
    this.installments = this.querySelector("[data-installments]");
    this.subscriptionDiscount = this.querySelector("[data-subscription-discount]");
    this.subscriptionCompare = this.querySelector("[data-subscription-compare]");
    this.subscriptionPriceMeta = this.querySelector("[data-subscription-price-meta]");
    this.subscriptionPrice = this.querySelector("[data-subscription-price]");
    this.oneTimePrice = this.querySelector("[data-one-time-price]");
    this.oneTimeCompare = this.querySelector("[data-one-time-compare]");
    this.oneTimeDiscount = this.querySelector("[data-one-time-discount]");
    this.oneTimePriceMeta = this.querySelector("[data-one-time-price-meta]");
    this.subscriptionPerDose = this.querySelector("[data-subscription-per-dose]");
    this.oneTimePerDose = this.querySelector("[data-one-time-per-dose]");
    this.subscriptionSavings = this.querySelector("[data-subscription-savings]");
    this.showSubscriptionSavingsPill = this.dataset.subscriptionShowSavingsPill !== "false";
    this.showSubscriptionCompareAt = this.dataset.subscriptionShowCompareAt !== "false";
    this.subscriptionSavingsPillText = this.dataset.subscriptionSavingsPillText || "Economize";
    this.subscriptionPricePerDoseLabel = this.dataset.subscriptionPricePerDoseLabel || "por dose";
    this.showOneTimeSavingsPill = this.dataset.oneTimeShowSavingsPill !== "false";
    this.showOneTimeCompareAt = this.dataset.oneTimeShowCompareAt !== "false";
    this.oneTimeSavingsPillText = this.dataset.oneTimeSavingsPillText || "Economize";
    this.oneTimePricePerDoseLabel = this.dataset.oneTimePricePerDoseLabel || "por dose";
    this.dosesPerPack = Number(this.dataset.dosesPerPack) || 0;
    this.quantityMode = this.dataset.quantityMode || "weight";
    this.hideQuantity = this.dataset.hideQuantity === "true";
    this.submitButton = this.querySelector("[data-submit]");
    this.form = this.querySelector("form");
    this.purchaseOptionIndex = this.parseOptionIndex(this.dataset.purchaseOptionIndex);
    const selectedWeightButton = this.querySelector("[data-quantity-option].is-selected");
    this.selectedQuantity = this.hideQuantity
      ? 1
      : selectedWeightButton
        ? Number(selectedWeightButton.dataset.quantityOption)
        : Number(this.quantityInput.value) || 1;
    this.selectedPurchaseOption = this.hasSubscriptionProduct ? "Assinatura Mensal" : "Compra única";
    this.syncQuantityInput();

    this.querySelectorAll("[data-quantity-option]").forEach((button) => {
      button.addEventListener("click", () => this.selectQuantity(button));
    });

    this.querySelectorAll("[data-purchase-option]").forEach((button) => {
      button.addEventListener("click", () => this.selectPurchaseOption(button));
    });

    if (this.form) {
      this.form.addEventListener("submit", () => this.updateVariant(), { capture: true });
    }

    this.updateVariant();
  }

  selectQuantity(button) {
    if (this.hideQuantity) return;
    this.selectedQuantity = Number(button.dataset.quantityOption);
    this.syncQuantityInput();
    this.toggleButtons("[data-quantity-option]", button);
    this.querySelectorAll("[data-quantity-option]").forEach((option) => {
      option.classList.toggle("is-highlighted", option === button);
    });
    this.updateVariant();
  }

  selectPurchaseOption(button) {
    this.selectedPurchaseOption = button.dataset.purchaseOption;
    this.toggleButtons("[data-purchase-option]", button);
    this.syncQuantityInput();
    this.updateVariant();
  }

  syncQuantityInput() {
    if (!this.quantityInput) return;

    const variant = this.getActiveVariant();
    this.quantityInput.value = variant ? this.getCartQuantity(variant) : this.isSubscriptionSelected() ? 1 : this.selectedQuantity;
  }

  getCartQuantity(variant) {
    if (!variant) return 1;
    return this.variantMatchesSelectedPackCount(variant) ? 1 : this.selectedQuantity;
  }

  getVariantTotal(variant) {
    if (!variant) return 0;
    return variant.price * (this.variantMatchesSelectedPackCount(variant) ? 1 : this.selectedQuantity);
  }

  getOneTimeTotal(variant) {
    return this.getVariantTotal(variant);
  }

  getActiveVariantTotal(variant = this.getActiveVariant()) {
    return this.getVariantTotal(variant);
  }

  variantMatchesSelectedPackCount(variant) {
    return this.getPackCountFromVariant(variant) === this.selectedQuantity;
  }

  toggleButtons(selector, selectedButton) {
    this.querySelectorAll(selector).forEach((button) => {
      const selected = button === selectedButton;
      button.classList.toggle("is-selected", selected);
      button.setAttribute("aria-pressed", selected);
    });
  }

  isSubscriptionSelected() {
    return this.normalize(this.selectedPurchaseOption) === "assinatura-mensal";
  }

  parseOptionIndex(value) {
    if (value == null || value === "") return null;
    const index = Number(value);
    return Number.isNaN(index) ? null : index;
  }

  getVariantOptionValues(variant) {
    if (!variant) return [];

    const values = [];
    if (variant.title) values.push(variant.title);
    if (Array.isArray(variant.options)) values.push(...variant.options);
    ["option1", "option2", "option3"].forEach((key) => {
      if (variant[key]) values.push(variant[key]);
    });

    return values;
  }

  isSubscriptionTypeVariant(variant) {
    return this.getVariantOptionValues(variant).some(
      (value) => this.normalize(value) === "assinatura-mensal",
    );
  }

  getPackCountFromVariant(variant) {
    if (!variant) return null;
    if (Number(variant.package_count) > 0) return Number(variant.package_count);

    for (const source of this.getVariantOptionValues(variant)) {
      const value = String(source);
      const packMatch = value.match(/(\d+)\s*pacotes?/i);
      if (packMatch) return Number(packMatch[1]);
      const productMatch = value.match(/(\d+)\s*produtos?/i);
      if (productMatch) return Number(productMatch[1]);
      const unitMatch = value.match(/(\d+)\s*unidades?/i);
      if (unitMatch) return Number(unitMatch[1]);
    }

    return null;
  }

  getSubscriptionVariant() {
    if (!this.subscriptionProduct?.variants?.length) return null;

    const variants = this.subscriptionProduct.variants;
    const byPackCount = variants.find(
      (variant) => this.getPackCountFromVariant(variant) === this.selectedQuantity,
    );
    if (byPackCount) return byPackCount;

    const baseVariant = variants.find((variant) => this.getPackCountFromVariant(variant) === 1);
    if (baseVariant) return baseVariant;

    const preferredVariant = variants.find(
      (variant) => variant.id === this.subscriptionVariantId && this.getPackCountFromVariant(variant) == null,
    );
    if (preferredVariant) return preferredVariant;

    return variants.find((variant) => this.getPackCountFromVariant(variant) == null && variant.available) ||
      variants.find((variant) => this.getPackCountFromVariant(variant) == null) ||
      variants.find((variant) => variant.available) ||
      variants[0];
  }

  isOneTimeVariant(variant) {
    if (!variant) return false;
    if (this.isSubscriptionTypeVariant(variant)) return false;

    if (this.purchaseOptionIndex == null) return true;

    const purchaseOption = this.normalize(variant.options?.[this.purchaseOptionIndex]);
    return purchaseOption === "compra-unica";
  }

  getOneTimeVariants() {
    return this.variants.filter((variant) => this.isOneTimeVariant(variant));
  }

  getOneTimeVariant() {
    const relatedPackage = this.relatedPackageProducts.find(
      (item) => Number(item.package_count) === this.selectedQuantity && item.variant,
    );
    if (relatedPackage && this.selectedQuantity > 1) {
      return { ...relatedPackage.variant, package_count: Number(relatedPackage.package_count) };
    }

    const oneTimeVariants = this.getOneTimeVariants();
    const byPackCount = (variants) =>
      variants.find((variant) => this.getPackCountFromVariant(variant) === this.selectedQuantity);

    const packMatch = byPackCount(oneTimeVariants);
    if (packMatch) return packMatch;

    const baseVariant = oneTimeVariants.find((variant) => this.getPackCountFromVariant(variant) === 1);
    if (baseVariant) return baseVariant;

    const singleVariantWithoutPack = oneTimeVariants.find(
      (variant) => this.getPackCountFromVariant(variant) == null,
    );
    if (singleVariantWithoutPack) return singleVariantWithoutPack;

    return (
      oneTimeVariants.find((variant) => variant.id === this.oneTimeVariantId && this.getPackCountFromVariant(variant) == null) ||
      oneTimeVariants[0] ||
      this.variants.find((variant) => !this.isSubscriptionTypeVariant(variant)) ||
      this.variants[0]
    );
  }

  getActiveVariant() {
    return this.isSubscriptionSelected() ? this.getSubscriptionVariant() : this.getOneTimeVariant();
  }

  updateVariant() {
    const variant = this.getActiveVariant();
    if (!variant) return;

    this.variantInput.value = variant.id;
    this.updatePrices();
    this.updateInstallments(variant);
    this.updateSubmitState(variant);
    this.syncStickyCart(variant);
  }

  updateSubmitState(variant = this.getActiveVariant()) {
    if (!this.submitButton || !variant) return;
    this.submitButton.toggleAttribute("disabled", !variant.available);
  }

  syncStickyCart(variant) {
    if (!variant) return;

    const stickyForm = document.querySelector(".sticky-cart__form form[data-type='add-to-cart-form']");
    if (!stickyForm) return;

    const stickyVariantInput = stickyForm.querySelector('input[name="id"]');
    if (stickyVariantInput) {
      stickyVariantInput.removeAttribute("disabled");
      stickyVariantInput.value = variant.id;
    }

    const stickyQuantityInput = stickyForm.querySelector('input[name="quantity"]');
    if (stickyQuantityInput) {
      stickyQuantityInput.value = this.getCartQuantity(variant);
    }
  }

  updateInstallments(variant = this.getActiveVariant()) {
    if (!variant || !this.installments) return;

    const total = this.getActiveVariantTotal(variant);
    const installment = total / 1;
    this.installments.innerHTML = `em até <strong>1x de ${this.formatMoney(installment)}</strong> sem juros`;
  }

  formatMoney(cents) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(cents / 100);
  }

  updatePerDose(totalCents, element, label) {
    if (!element || !this.dosesPerPack) return;

    const dosesTotal = this.dosesPerPack * this.selectedQuantity;
    const perDose = totalCents / dosesTotal;
    element.textContent = `${this.formatMoney(perDose)} ${label}`;
  }

  findVariant(purchaseOption) {
    const normalizedPurchaseOption = this.normalize(purchaseOption);

    if (this.purchaseOptionIndex != null) {
      const indexedVariant = this.variants.find(
        (item) => this.normalize(item.options[this.purchaseOptionIndex]) === normalizedPurchaseOption,
      );
      if (indexedVariant) return indexedVariant;
    }

    return this.variants.find((item) =>
      item.options.some((option) => this.normalize(option) === normalizedPurchaseOption),
    );
  }

  updatePrices() {
    const oneTimeVariant = this.getOneTimeVariant();
    const subscriptionVariant = this.getSubscriptionVariant();

    if (oneTimeVariant) {
      const priceMultiplier = this.variantMatchesSelectedPackCount(oneTimeVariant) ? 1 : this.selectedQuantity;
      const oneTimeCompareAtPrice = oneTimeVariant.compare_at_price || oneTimeVariant.price;
      const oneTimeOriginalPrice = Math.max(oneTimeVariant.price, oneTimeCompareAtPrice) * priceMultiplier;
      const oneTimeSalePrice = Math.min(oneTimeVariant.price, oneTimeCompareAtPrice) * priceMultiplier;
      const oneTimeDiscountPercent =
        oneTimeOriginalPrice > oneTimeSalePrice
          ? Math.round(((oneTimeOriginalPrice - oneTimeSalePrice) * 100) / oneTimeOriginalPrice)
          : 0;

      if (this.oneTimePrice) this.oneTimePrice.textContent = this.formatMoney(oneTimeSalePrice);
      const oneTimeHasDiscount = oneTimeOriginalPrice > oneTimeSalePrice;
      if (this.oneTimeCompare) {
        this.oneTimeCompare.textContent = this.formatMoney(oneTimeOriginalPrice);
        this.oneTimeCompare.hidden = !this.showOneTimeCompareAt || !oneTimeHasDiscount;
      }
      if (this.oneTimeDiscount) {
        this.oneTimeDiscount.textContent = `${this.oneTimeSavingsPillText} ${oneTimeDiscountPercent}%`;
        this.oneTimeDiscount.hidden = !this.showOneTimeSavingsPill || !oneTimeHasDiscount;
      }
      if (this.oneTimePriceMeta) {
        this.oneTimePriceMeta.hidden = Boolean(this.oneTimeCompare?.hidden && this.oneTimeDiscount?.hidden);
      }
      this.updatePerDose(oneTimeSalePrice, this.oneTimePerDose, this.oneTimePricePerDoseLabel);
    }

    if (!subscriptionVariant) return;

    const subscriptionCompareAtPrice = subscriptionVariant.compare_at_price || subscriptionVariant.price;
    const subscriptionOriginalPrice = Math.max(subscriptionVariant.price, subscriptionCompareAtPrice);
    const subscriptionSalePrice = Math.min(subscriptionVariant.price, subscriptionCompareAtPrice);
    const subscriptionMultiplier = this.variantMatchesSelectedPackCount(subscriptionVariant) ? 1 : this.selectedQuantity;
    const subscriptionTotal = subscriptionSalePrice * subscriptionMultiplier;
    const subscriptionCompareTotal = subscriptionOriginalPrice * subscriptionMultiplier;
    const discountPercent =
      subscriptionOriginalPrice > subscriptionSalePrice
        ? Math.round(((subscriptionOriginalPrice - subscriptionSalePrice) * 100) / subscriptionOriginalPrice)
        : 0;

    if (this.subscriptionCompare) {
      this.subscriptionCompare.textContent = this.formatMoney(subscriptionCompareTotal);
      this.subscriptionCompare.hidden = !this.showSubscriptionCompareAt || subscriptionCompareTotal <= subscriptionTotal;
    }
    if (this.subscriptionPrice) {
      this.subscriptionPrice.textContent = this.formatMoney(subscriptionTotal);
    }
    if (this.subscriptionDiscount) {
      this.subscriptionDiscount.textContent = `${this.subscriptionSavingsPillText} ${discountPercent}%`;
      this.subscriptionDiscount.hidden = !this.showSubscriptionSavingsPill || subscriptionCompareTotal <= subscriptionTotal;
    }
    if (this.subscriptionPriceMeta) {
      this.subscriptionPriceMeta.hidden = Boolean(this.subscriptionCompare?.hidden && this.subscriptionDiscount?.hidden);
    }
    this.updatePerDose(subscriptionTotal, this.subscriptionPerDose, this.subscriptionPricePerDoseLabel);
    this.updateSavings(subscriptionCompareTotal - subscriptionTotal);
  }

  updateSavings(savings) {
    if (!this.subscriptionSavings) return;
    this.subscriptionSavings.textContent = `economia de ${this.formatMoney(Math.max(savings, 0))}`;
  }

  normalize(value = "") {
    return String(value)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/\s+/g, "-");
  }
}

if (!customElements.get("pdp-03-balance-subscription-purchase")) {
  customElements.define("pdp-03-balance-subscription-purchase", SubscriptionPurchase);
}
